import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, ExternalLink, MapPin, Star, TrendingUp, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

interface EventRow {
  id: string;
  event_name: string;
  status: string;
  start_date: string;
  end_date: string | null;
  location: string | null;
  carr_relevance_tier: string | null;
}

const TIER_BADGE: Record<string, { color: string; label: string }> = {
  'Tier 1: Must-Attend': { color: 'bg-red-100 text-red-700 border-red-200', label: 'T1' },
  'Tier 2: High-Value': { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'T2' },
  'Tier 3: Monitor': { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'T3' },
};

export function UpcomingEventsCard() {
  const { data: events, isLoading } = useQuery({
    queryKey: ['dashboard-upcoming-events'],
    queryFn: async (): Promise<EventRow[]> => {
      const { data, error } = await (supabase as any)
        .from('events')
        .select('id, event_name, status, start_date, end_date, location, carr_relevance_tier')
        .gte('start_date', new Date().toISOString().split('T')[0])
        .in('status', ['upcoming', 'attending', 'watching'])
        .order('start_date', { ascending: true })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const formatDateRange = (start: string, end: string | null) => {
    const s = format(new Date(start + 'T00:00:00'), 'MMM d, yyyy');
    if (!end) return s;
    const e = format(new Date(end + 'T00:00:00'), 'MMM d, yyyy');
    return `${s} – ${e}`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Upcoming Events</CardTitle>
          </div>
          <Link to="/events">
            <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : !events?.length ? (
          <p className="text-sm text-muted-foreground text-center py-6">No upcoming events</p>
        ) : (
          <div className="space-y-1">
            {events.map((event) => {
              const tier = event.carr_relevance_tier ? TIER_BADGE[event.carr_relevance_tier] : null;
              return (
                <Link key={event.id} to={`/events/${event.id}`} className="block p-2.5 rounded-lg hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-2">
                    {tier && (
                      <Badge variant="outline" className={`text-[10px] px-1.5 ${tier.color}`}>
                        {tier.label}
                      </Badge>
                    )}
                    <p className="text-sm font-medium truncate flex-1">{event.event_name}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{formatDateRange(event.start_date, event.end_date)}</span>
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{event.location}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        <div className="pt-3 text-center">
          <Link to="/events" className="text-xs text-primary hover:underline font-medium">
            View All Events →
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
